import {
  Box,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Call, Send, Mail } from '@mui/icons-material';
import { ccfCallActions, updateCallType } from '@nice-ccf/ui-components';
import {
  CcfAvailableIcon,
  CcfCallTransferIcon,
  CcfFavouriteIcon,
  CCfToggleIconButton,
  CcfWorkingIcon,
  useTranslator,
  withHover,
  withHoverProps,
  CcfConversationIcon,
  CcfButton,
  CcfDisableCallIcon,
  CcfBox,
  CcfSnackBar,
  CcfAlert,
  CcfTextField,
} from '@nice-ccf/ui-controls';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Transition } from 'react-transition-group';
import { Agent, AgentStatus } from '../+state/ccf-directory.slice';
import {
  activeContactSelector,
  updateActiveContact,
  updateAppSpaceTab,
} from '../../ccf-inbox/ccf-inbox.slice';
import { ccfConversationsActions } from '../../ccf-mocks/ccf-app-agent-messages/+state/ccf-conversations.slice';
import classes from '../ccf-directory.module.scss';
import { AppSpace } from '../../../enums/app-space-tabs';
import { VoiceContactStatus } from '@nice-ccf/core-sdk';

type size = 'small' | 'medium' | 'large' | undefined;

/**
 * Component to be used for directory item
 * @param props - Agent
 * @example <DirectoryItem />
 * @returns
 */
const DirectoryItem = (props: { user: Agent } & withHoverProps) => {
  const { user, isHovering, onMouseEnter, onMouseOut } = props;
  const [quickChat, updateQuickChat] = useState({ isOpen: false, message: '' });
  const activeContact = useSelector(activeContactSelector);
  const [deboucedHover, updatedeboucedHover] = useState(false);
  const [translate] = useTranslator();
  const { addConsultUser } = ccfCallActions;
  const { updateSelectedConversation } = ccfConversationsActions;
  const dispatch = useDispatch();
  const agentStatus = translate(user.status);
  const quickChatPlaceHolder = translate('quickChatPlaceHolder');
  const theme = useTheme();
  const [showCallTransferedToast, setshowCallTransferedToast] = useState(false);
  const [CallTransferredMessage, setCallTransferredMessage] = useState('');

  const styles = {
    displayFlex: 'flex',
    flexDirection: 'row',
    paddingX: 1.5,
    paddingY: 1,
    padding: 1.5,
    boxMaxWidth: '100%',
    alignItemsCenter: 'center',
    justifyContentStart: 'flex-start',
    justifyContentBetween: 'space-between',
    size: 'small' as size,
    iconSize: 'small' as size,
  };

  useEffect(() => {
    const timeout = setTimeout(
      () => updatedeboucedHover((prevState) => isHovering),
      1000
    );
    return () => {
      updatedeboucedHover((prevState) => false);
      return clearTimeout(timeout);
    };
  }, [isHovering]);

  /**
   * Function to add user to conference
   * @param user - Agent
   * @example addUsertoConference(user)
   */
  function addUsertoConference(user: Agent) {
    dispatch(addConsultUser(user));
    dispatch(updateCallType('conference'));
  }

  /**
   * Function to Initiate Call Transfer
   * @param user - Agent
   * @example initiateCallTransfer(user)
   */
  function initiateCallTransfer(user: Agent) {
    if (activeContact) {
      setTimeout(() => {
        dispatch(
          updateActiveContact({
            id: activeContact?.id || '',
            updates: { contactStatus: VoiceContactStatus.DISCONNECTED },
          })
        );
      }, 1000);
      setCallTransferredMessage(`Call Transferred to ${user.name}`);
      setshowCallTransferedToast(true);
    }
  }

  /**
   * Used to close the snackbar
   * @param event -React.SyntheticEvent
   * @param reason -string
   * @example - handleClose()
   */
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setshowCallTransferedToast(false);
    setCallTransferredMessage('');
  };

  /**
   * Function to open quick chat
   * @example openQuickChat()
   */
  function openQuickChat() {
    updateQuickChat((prevState) => ({
      ...prevState,
      isOpen: !prevState.isOpen,
    }));
  }

  /**
   * Function to update quick chat message
   * @param event - React.ChangeEvent
   * @example updateQuickChatMessage(event)
   */
  function updateQuickChatMessage(
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) {
    updateQuickChat((prevState) => ({
      ...prevState,
      message: event.target.value,
    }));
  }

  /**
   * Function to inititateConversation
   * @param user -Agent
   * @example initiateConversation(user)
   */
  const initiateConversation = (user: Agent) => {
    dispatch(updateSelectedConversation({ user }));
    dispatch(updateAppSpaceTab({ tab: AppSpace.CONVERSATIONS }));
  };

  const defaultStyles = {
    height: 85,
    opacity: 1,
    transition: `height 250ms ease-in`,
    background: isHovering ? '#ecf5fa' : 'inherit',
    overflow: 'hidden',
  };

  const transitionStyles = {
    entering: {},
    entered: { height: 130, opacity: 1 },
    exiting: { height: 85, opacity: 1 },
    exited: { height: 85, opacity: 1 },
    unmounted: { height: 85, opacity: 1 },
  };

  const fontSizes: { small: 'small'; smaller: 'smaller' } = {
    small: 'small',
    smaller: 'smaller',
  };

  return (
    <>
      <Transition in={deboucedHover} timeout={{ enter: 500, exit: 0 }}>
        {(state) => (
          <Box
            style={{
              ...defaultStyles,
              ...transitionStyles[state],
            }}
            component={'li'}
            className={classes.directoryItem}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseOut}
          >
            <Box className={classes.directoryUser}>
              <Typography noWrap className={classes.text}>
                {user.name}
              </Typography>
              <CCfToggleIconButton
                classes={{ root: classes.hoverDisabledButton }}
                icon={<CcfFavouriteIcon htmlColor="#FDE000" />}
                isToggled={user.isFavourite}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                size={fontSizes.small}
                toggleIcon={<CcfFavouriteIcon htmlColor="#DAE2E8" />}
              />
            </Box>
            <Box className={classes.directoryItemUserStatus}>
              <Box
                display={styles.displayFlex}
                alignItems={styles.alignItemsCenter}
                minHeight={40}
              >
                {user.status === 'available' ? (
                  <CcfAvailableIcon className={classes.icon} />
                ) : (
                  <CcfWorkingIcon className={classes.icon} />
                )}
                <Typography noWrap className={classes.text}>
                  {agentStatus}
                </Typography>
              </Box>
              {isHovering && (
                <Box
                  display={styles.displayFlex}
                  justifyContent={styles.justifyContentStart}
                  flex="1"
                  ml={1}
                >
                  <CcfButton
                    color="secondary"
                    classes={{ textSecondary: classes.textSecondary }}
                    size={styles.size}
                    onClick={() => initiateConversation(user)}
                  >
                    <CcfConversationIcon />
                  </CcfButton>
                  <CcfButton
                    color="secondary"
                    classes={{ textSecondary: classes.textSecondary }}
                    className={clsx(user.status === AgentStatus.WORKING)}
                    size={styles.size}
                    disabled={user.status === AgentStatus.WORKING}
                    onClick={() => addUsertoConference(user)}
                  >
                    {user.status === AgentStatus.WORKING ? (
                      <CcfDisableCallIcon fontSize={styles.iconSize} />
                    ) : (
                      <Call fontSize={styles.iconSize} />
                    )}
                  </CcfButton>
                  <CcfButton
                    color="secondary"
                    classes={{ textSecondary: classes.textSecondary }}
                    size={styles.size}
                  >
                    <Mail fontSize={styles.iconSize} />
                  </CcfButton>
                  <CcfButton
                    color="secondary"
                    classes={{ textSecondary: classes.textSecondary }}
                    size={styles.size}
                    disabled={user.status === AgentStatus.WORKING}
                    onClick={() => initiateCallTransfer(user)}
                  >
                    <CcfCallTransferIcon
                      viewBox="0 0 25 20"
                      fontSize={styles.iconSize}
                      style={{
                        opacity:
                          user.status === AgentStatus.WORKING ? '0.2' : '1',
                      }}
                    />
                  </CcfButton>
                </Box>
              )}
            </Box>
            {deboucedHover && (
              <Box display={styles.displayFlex} width={styles.boxMaxWidth}>
                <CcfTextField
                  id="quick chat message"
                  size={fontSizes.small}
                  fullWidth
                  style={{
                    marginRight: 4,
                    flex: 1,
                    background: theme.palette.background.paper,
                    fontSize: fontSizes.smaller,
                    overflow: 'hidden',
                  }}
                  placeholder={quickChatPlaceHolder}
                  variant="outlined"
                  value={quickChat.message}
                  InputProps={{ classes: { input: classes.input1 } }}
                  onChange={updateQuickChatMessage}
                />
                <IconButton
                  color="primary"
                  size={fontSizes.small}
                  disabled={quickChat.message.length === 0}
                >
                  <Send fontSize={fontSizes.small} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Transition>
      <CcfBox>
        <CcfSnackBar
          onClose={() => handleClose()}
          open={showCallTransferedToast}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <div>
            <CcfAlert closeAlert={handleClose} severity="success">
              {CallTransferredMessage}
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
    </>
  );
};

export default withHover(DirectoryItem);
